import React, { useCallback, useMemo, useState } from 'react';
import classes from './style.module.scss';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';
import AccessControl from '~/accessControl';
import Button from '~/app/common/Button';
import { useStoreActions, useStoreState } from '~/store';
import useTranslation from '~/app/hooks/useTranslation';
import { APIResponse, ServiceDetails, ServiceSavePayload } from '~/types';
import CompleteService from '~/services/CompleteService';
import { useHistory } from 'react-router';
import { route } from '~/app/helpers/route';
import { ROUTES } from '~/app/Router';
import { notify } from '~/app/helpers/notify';
import { ServiceStatuses } from '~/app/data/statuses/services';

export interface CompleteServiceDialogProps {
    isWorkOrderChecklist?: boolean;
}

export type CompleteServiceDialogComponent = React.FC<CompleteServiceDialogProps>;

const CompleteServiceDialog: CompleteServiceDialogComponent = ({ isWorkOrderChecklist = false }) => {
    const { t } = useTranslation('serviceChecklist');
    const { t: tIssue } = useTranslation('issue');
    const { t: tService } = useTranslation('service');
    const history = useHistory();
    const [isOpenCloseServiceForm, setIsOpenCloseServiceForm] = useState<boolean>(false);
    const serviceDetails = useStoreState(state => state.service.data);
    const fetching = useStoreState(state => state.serviceCheck.fetching);
    const isChecksDataUpdated = useStoreState(state => state.serviceCheck.isChecksDataUpdated);
    const setShowCompleted = useStoreActions(actions => actions.service.setShowCompleted);
    const saveServiceAction = useStoreActions(actions => actions.service.saveService);
    const fetchService = useStoreActions(actions => actions.service.fetchService);
    const checksData = useStoreState(state => state.serviceCheck.checksData);
    const userName = useStoreState(state => state.user.data.name);
    const hasSimplifiedWorkorder = useStoreState(state => state.service.hasSimplifiedWorkorder ?? false);
    const serviceStatus = useStoreState(state => state.service.data.status);
    const workOrderNo = useStoreState(state => state.service.data.workOrderNo);

    const isServiceChecklistEnabled = useMemo(() => {
        return (hasSimplifiedWorkorder || isWorkOrderChecklist) && ((serviceStatus === ServiceStatuses.TRIGGERED) || (serviceStatus === ServiceStatuses.INPROGRESS && workOrderNo));
    }, [hasSimplifiedWorkorder, serviceStatus, workOrderNo, isWorkOrderChecklist]);

    const closeServiceClickHandler = useCallback((state: boolean) => (): void => {
        setIsOpenCloseServiceForm(state);
    }, []);

    const fetchServiceDetails = useCallback(async (id: ServiceDetails['id']) => {
        fetchService(id).then(() => {
            setShowCompleted(true);
            history.length > 1 ? history.goBack() : history.push(route(ROUTES.projects));
        });
    }, [fetchService, setShowCompleted, history]);

    const saveServiceClickHandler = useCallback(() => {
        if (!isServiceChecklistEnabled) {
            return;
        }

        const serviceId = serviceDetails.id;
        const workOrderId = serviceDetails.workOrderId;

        const payload: ServiceSavePayload = {
            ...(serviceId && { serviceId }),
            ...(workOrderId && { workOrderId }),
            checklist: checksData,
            isDraft: 1
        } as ServiceSavePayload;
        
        saveServiceAction(payload).then(async (data: APIResponse) => {
            if (data.success && serviceId) {
                fetchService(serviceId).then(() => {
                    notify(tService(`success.saved`), 'success');
                });
            } else {
                notify(tIssue(`error.${data?.msg}`, {userName: data?.msg && data.msg === 'userAlreadyExistForSelectedDate' ? userName : ''}), 'error');
            }

        });
    }, [checksData, saveServiceAction, fetchService, serviceDetails.id, serviceDetails.workOrderId, tIssue, tService, userName, isServiceChecklistEnabled])

    const back = useCallback(() => {
        history.length > 1 ? history.goBack() : history.push(route(ROUTES.projects));
    }, [history]);

    return (
        <>
            <AccessControl requiredPermissions={PERMISSION_GROUPS.writeServiceDetails} >
                <div className={`${classes['footer']}`}>
                    <Button className={`${classes['abort']}`} onClick={back}> {t('cancel')} </Button>
                    <Button
                        loading={fetching}
                        className={classes['saveChecklist']}
                        onClick={saveServiceClickHandler}
                        disabled={checksData.length === 0 || !isServiceChecklistEnabled}
                    >
                        {t('temporary')}
                    </Button>
                    <Button
                        loading={fetching}
                        className={classes['submit']}
                        onClick={closeServiceClickHandler(true)}
                        disabled={!isChecksDataUpdated || !isServiceChecklistEnabled}
                    >
                        {t('complete')} 
                    </Button>
                </div>
                <CompleteService open={isOpenCloseServiceForm} onClose={closeServiceClickHandler(false)} serviceDetails={serviceDetails} onSave={fetchServiceDetails} isWorkOrderChecklist={isWorkOrderChecklist}/>
            </AccessControl>
        </>
    );
};


export default CompleteServiceDialog;